import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { FormattedMessage, useIntl } from 'react-intl'
import Info from 'components/shared/Info'
import CardDetailRow from './CardDetailRow'
import Loader from 'components/shared/Loader'
import moment from 'moment/moment'
import { CartItemCardDetailDto } from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { CardSectionImageWrapper } from './styles'
import { setIsErrorModalOpened } from 'store/general-slice'
import { useAppDispatch } from 'store/hooks'
import { CONTACT_LINK } from 'constants/links'

interface CardSectionDetail extends CartItemCardDetailDto {
    personDescription?: string
    cartItemId?: string
    expirationDate?: string
}
interface CardSectionDetailsProps {
    cardBox: CardSectionDetail
    isRenewed?: boolean
}

const CardSectionDetails = ({
    cardBox,
    isRenewed,
}: CardSectionDetailsProps) => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { language } = useApp()

    const {
        cartItemId,
        personDescription,
        dateOfBirth,
        email,
        firstName,
        phone,
        lastName,
        expirationDate,
    } = cardBox

    const estimatedExpirationDate = moment()
        .add(379, 'days')
        .format('DD.MM.YYYY')
    const calculatedExpirationDate = expirationDate || estimatedExpirationDate

    return (
        <>
            <Box
                direction={['column', 'row']}
                gap={[32, 40]}
                mt={[32, 40]}
                mb={[32, 40]}
                justify={'center'}
                align={'center'}
            >
                {!isRenewed && (
                    <CardSectionImageWrapper justify={'center'} isFullWidth>
                        <Loader />
                        {!!cartItemId && (
                            <img
                                src={`${
                                    process.env.REACT_APP_API_URL
                                    // timestamped to avoid caching images after changes
                                }api/${language}/cart/item/card/image/${cartItemId}?${Date.now()}`}
                                alt=""
                                onError={() => {
                                    dispatch(setIsErrorModalOpened(true))
                                }}
                            />
                        )}
                    </CardSectionImageWrapper>
                )}
                <Box direction="column" isFullWidth gap={16}>
                    {!!personDescription && (
                        <CardDetailRow
                            title={formatMessage({
                                id: 'tickets.edit_guest_type_label',
                                defaultMessage: 'Typ gościa',
                            })}
                        >
                            {personDescription}
                        </CardDetailRow>
                    )}
                    {!!calculatedExpirationDate && (
                        <CardDetailRow
                            title={formatMessage({
                                id: 'annual.summary.card_expiration_date',
                                defaultMessage: 'Data ważności karty',
                            })}
                        >
                            {calculatedExpirationDate}{' '}
                            {!isRenewed && (
                                <Text
                                    as="span"
                                    variant="bodySmall"
                                    color="grayscale_700"
                                >
                                    {formatMessage({
                                        id: 'annual.summary.or_one_year_from_first_visit',
                                        defaultMessage: '(lub rok od 1 wizyty)',
                                    })}
                                </Text>
                            )}
                        </CardDetailRow>
                    )}
                    {!!firstName && !!lastName && (
                        <CardDetailRow
                            title={formatMessage({
                                id: 'global.input.full_name',
                                defaultMessage: 'Imię i nazwisko',
                            })}
                        >{`${firstName} ${lastName}`}</CardDetailRow>
                    )}
                    {!!dateOfBirth && (
                        <CardDetailRow
                            title={formatMessage({
                                id: 'global.input.birth_date',
                                defaultMessage: 'Data urodzenia',
                            })}
                        >
                            {moment(dateOfBirth, 'YYYY-MM-DD').format(
                                'DD.MM.YYYY'
                            )}
                        </CardDetailRow>
                    )}
                    {!!email && (
                        <CardDetailRow
                            title={formatMessage({
                                id: 'global.input.email_label',
                                defaultMessage: 'Adres e-mail',
                            })}
                        >
                            {email}
                        </CardDetailRow>
                    )}
                    {!!phone && (
                        <CardDetailRow
                            title={formatMessage({
                                id: 'global.input.phone_label',
                                defaultMessage: 'Numer telefonu',
                            })}
                            isLast
                        >
                            {phone}
                        </CardDetailRow>
                    )}
                </Box>
            </Box>
            {isRenewed && (
                <Box direction="row">
                    <Text
                        variant="bodySmall"
                        color="grayscale_900"
                        mb={[16, 24]}
                        mr={4}
                    >
                        <FormattedMessage
                            id="annual.renew.summary_details_problems"
                            defaultMessage="Jeśli dane są nieaktualne, <span>skontaktuj się z nami</span>"
                            description="Keep the html tags in the translation"
                            values={{
                                span: (chunks: string[]) => (
                                    <>
                                        <Text
                                            as="a"
                                            variant="bodySmall"
                                            color="grayscale_900"
                                            isUnderlined
                                            onClick={() =>
                                                window.open(
                                                    CONTACT_LINK,
                                                    '_blank'
                                                )
                                            }
                                            data-testid="renew-details-outdated-redirect-contact"
                                        >
                                            {chunks}
                                        </Text>
                                    </>
                                ),
                            }}
                        />
                    </Text>
                </Box>
            )}
            <Info color="info">
                {isRenewed
                    ? formatMessage({
                          id: 'annual.summary.card_activation_info_renewed',
                          defaultMessage:
                              'Kartę aktywujemy automatycznie, kiedy poprzednia straci ważność.',
                      })
                    : formatMessage({
                          id: 'annual.summary.card_activation_info',
                          defaultMessage:
                              'Kartę aktywujemy podczas Twojej najbliższej wizyty w parku. Jeśli nie odwiedzisz parku w ciągu 14 dni od zakupu karty, po tym czasie zostanie ona automatycznie aktywowana.',
                      })}
            </Info>
        </>
    )
}

export default CardSectionDetails
